import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";

import { deleteTag, getTags } from "src/api/settings";
import CustomModal from "src/components/CustomModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import NewTagModal from "src/components/modals/NewTagModal";
import PermissionWrapper from "src/components/PermissionWrapper";
import { Button } from "src/components/ui/button";
import { EmptyState, SettingsPanel } from "src/components/common";
import { showSuccessToast } from "src/lib/utils";

const AddTagButton = () => (
	<PermissionWrapper permission="add_tag">
		<CustomModal
			title="Add New Tag"
			trigger={
				<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
					<Plus className="tw-h-4 tw-w-4" />
					Add Tag
				</Button>
			}
		>
			<NewTagModal />
		</CustomModal>
	</PermissionWrapper>
);

const TagSettings = () => {
	const queryClient = useQueryClient();
	const { data } = useQuery({ queryKey: ["tags"], queryFn: getTags, suspense: true });

	const { mutate: deleteMutate } = useMutation(deleteTag, {
		onSuccess: ({ message }) => {
			queryClient.invalidateQueries(["tags"]);
			showSuccessToast(message || "Tag deleted successfully");
		},
	});

	const tags = data?.data ?? [];

	return (
		<SettingsPanel title="Tag Settings" description="Categorise bank accounts and settlements." actions={<AddTagButton />}>
			{tags.length === 0 ? (
				<EmptyState title="No tags yet" description="Create a tag to categorise your bank accounts." action={<AddTagButton />} />
			) : (
				<div className="tw-flex tw-flex-wrap tw-gap-2">
					{tags.map(({ uuid, name }) => (
						<span
							key={uuid}
							className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-border tw-bg-bg tw-py-1 tw-pl-3 tw-pr-1.5 tw-text-[13px] tw-font-medium tw-text-text"
						>
							{name}
							<PermissionWrapper permission="delete_tag">
								<CustomConfirmDialog
									trigger={
										<button
											type="button"
											aria-label={`Delete tag ${name ?? ""}`}
											className="tw-rounded-full tw-p-1 tw-text-text-soft tw-transition-colors hover:tw-bg-red-tint hover:tw-text-red"
										>
											<X className="tw-h-3.5 tw-w-3.5" />
										</button>
									}
									mutateFn={() => deleteMutate({ uuid: uuid ?? "" })}
								/>
							</PermissionWrapper>
						</span>
					))}
				</div>
			)}
		</SettingsPanel>
	);
};

export default TagSettings;
